import {TraceApiEndpointBuilder} from '../traceApi';
import {IDeviceInfo} from './sessions.endpoint';

export type MtnSubscriptionStatus = 'ACTIVE' | 'PENDING' | 'CANCELLED';

export interface MtnRedirectToPenrose {
  action: 'redirect_to_penrose';
  reason: string;
  url?: string;
}

export interface MtnAllowLoginResponse {
  action: 'allow_login';
  accessToken: string;
  user: {
    id: string | null;
    carrier: string;
    lang?: string;
  };
  subscriptionStatus: MtnSubscriptionStatus;
  contentAccessEndDate: string;
  subscribeUrl?: string;
  mtnToken?: string; // only when backend runs with MTN_DB_DISABLED
  session?: {
    _id: string;
    userId: string;
    deviceInfo?: IDeviceInfo;
    createdAt: string;
    lastActivity: string;
    expiresAt: string;
  };
}

// MTN Request OTP types
export interface MtnRequestOtpRequest {
  msisdn: string; // 233XXXXXXXXX
}

export type MtnRequestOtpResponse =
  | {action: 'otp_sent'}
  | MtnRedirectToPenrose
  | {action: 'otp_send_failed'; reason: string}
  | MtnAllowLoginResponse; // only with MTN_LOGIN_WITHOUT_OTP (testing)

// MTN Verify OTP types
export interface MtnVerifyOtpRequest {
  msisdn: string;
  otp: string;
  deviceInfo?: IDeviceInfo;
}

export type MtnVerifyOtpResponse =
  | MtnAllowLoginResponse
  | {action: 'otp_invalid'; reason: 'valid' | 'invalid' | 'used' | 'expired'};

// MTN Check Return types (user coming back from the Penrose subscription page)
export interface MtnCheckReturnRequest {
  status: string;
  msisdn: string;
  transactionID?: string;
  deviceInfo?: IDeviceInfo;
}

export type MtnCheckReturnResponse =
  | MtnAllowLoginResponse
  | MtnRedirectToPenrose;

// MTN Refresh types
export type MtnRefreshResponse =
  | {action: 'refreshed'; subscriptionStatus: MtnSubscriptionStatus}
  | MtnRedirectToPenrose;

export interface MtnUnsubscribeResponse {
  action: 'unsubscribed';
}

export interface MtnTokenArg {
  mtnToken?: string;
}

// The mtnToken replaces the Firebase token and the session header.
const mtnTokenAuth = (arg: MtnTokenArg | void) =>
  arg?.mtnToken
    ? {
        noAuth: true,
        noSession: true,
        headers: {Authorization: `Bearer ${arg.mtnToken}`},
      }
    : {};

export const createMtnEndpoint = (builder: TraceApiEndpointBuilder) => ({
  // Request OTP for MTN MSISDN login
  mtnRequestOtp: builder.mutation<MtnRequestOtpResponse, MtnRequestOtpRequest>(
    {
      query: body => ({
        url: '/auth/mtn/request-otp',
        method: 'POST',
        body,
        noAuth: true,
        skipAuthGuard: true,
      }),
    },
  ),

  // Verify OTP and authenticate
  mtnVerifyOtp: builder.mutation<MtnVerifyOtpResponse, MtnVerifyOtpRequest>({
    query: body => ({
      url: '/auth/mtn/verify-otp',
      method: 'POST',
      body,
      noAuth: true,
      skipAuthGuard: true,
    }),
  }),

  // Check status when returning from the Penrose subscription page
  mtnCheckReturn: builder.mutation<
    MtnCheckReturnResponse,
    MtnCheckReturnRequest
  >({
    query: body => ({
      url: '/auth/mtn/check-return',
      method: 'POST',
      body,
      noAuth: true,
      skipAuthGuard: true,
    }),
  }),

  // Refresh MTN subscription (backend reads claims from Firebase).
  // Testing (MTN_DB_DISABLED): pass the mtnToken from login instead.
  mtnRefresh: builder.mutation<MtnRefreshResponse, MtnTokenArg | void>({
    query: arg => ({
      url: '/auth/mtn/refresh',
      method: 'POST',
      body: {},
      skipAuthGuard: true,
      ...mtnTokenAuth(arg),
    }),
  }),

  // Unsubscribe from MTN
  mtnUnsubscribe: builder.mutation<MtnUnsubscribeResponse, MtnTokenArg | void>(
    {
      query: arg => ({
        url: '/auth/mtn/unsubscribe',
        method: 'POST',
        body: {},
        ...(arg?.mtnToken ? {skipAuthGuard: true} : {}),
        ...mtnTokenAuth(arg),
      }),
    },
  ),
});
